import { Injectable, Pipe, PipeTransform, inject } from '@angular/core';
import { AssetDto, AssetVersionDto } from '@nusign/picflow-backend-types';
import { Rect } from '../../classes/rect.class';
import { assetSizes, sizesToAlwaysIncludeMetadataFor, specialCaseFormats } from '../../consts';
import { getAssetDimentions } from '../../gallery/lib/get-asset-dimentions';
import { getMaxWebGlTextureSize } from '../../gallery/lib/max-webgl-texture-size';
import { calcBaseUrl } from '../../lib/calc-base-url';
import { calcResizedUrl } from '../../lib/calc-resized-url';
import { canDisplayAsset } from '../../lib/can-display-asset';
import { devicePixelRatio } from '../../lib/device-pixel-ratio';
import { getExtension } from '../../lib/get-extension';
import { PICFLOW_CONFIG, PicflowConfig } from '../../picflow-config';
import { UrlAutoUpscaleService } from '../../services/url-auto-upscale.service';

@Pipe({
  name: 'assetVersionUrl',
  standalone: true,
})
@Injectable({
  providedIn: 'root',
})
export class AssetVersionUrlPipe implements PipeTransform {
  private config = inject<PicflowConfig>(PICFLOW_CONFIG);
  private urlUpscaleService = inject(UrlAutoUpscaleService);

  public getTargetDimensions(
    uuid: string,
    target: Rect,
    source: Rect,
    options: { forCanvas?: boolean; allowUpScale?: boolean } = {}
  ) {
    const targetLongEdge = Math.max(target.width, target.height) * devicePixelRatio();

    if (source.width === 0) throw 'Invalid source width';
    if (source.height === 0) throw 'Invalid source height';
    const aspect = source.height / source.width;

    const assetSizesIncludingInfinity = [...assetSizes, Infinity];
    let index = assetSizesIncludingInfinity.length - 1;
    while (assetSizesIncludingInfinity[index - 1] >= targetLongEdge * 0.9) index -= 1;
    if (index < 0) index = 0;

    let width: number;
    let height: number;

    if (aspect < 1) {
      // Landscape
      width = assetSizesIncludingInfinity[index];
      height = width * aspect;
    } else {
      // Portrait
      height = assetSizesIncludingInfinity[index];
      width = height / aspect;
    }

    // Reducing size when needed (used to avoid webgl limit)
    // TODO: implement SplitTexture to allow bigger textures to be loaded.
    if (options.forCanvas) {
      const maxSize = getMaxWebGlTextureSize();
      const maxSizeClamped = assetSizesIncludingInfinity
        .slice()
        .reverse()
        .find(size => size < maxSize);
      if (aspect <= 1 && width > maxSize) {
        width = maxSizeClamped;
        height = width * aspect;
        console.warn('Asset resolution limited by MAX_TEXTURE_SIZE');
      }
      if (aspect >= 1 && height > maxSize) {
        height = maxSizeClamped;
        width = height / aspect;
        console.warn('Asset resolution limited by MAX_TEXTURE_SIZE');
      }
    }

    if (isNaN(width) || isNaN(height)) {
      // eslint-disable-next-line no-debugger
      debugger;
      console.error('Invalid width, height or quality');
      return { width: assetSizes[0], height: assetSizes[0] };
    }

    return this.urlUpscaleService.get(uuid, width, height, options.allowUpScale);
  }

  public shouldUseBase(sourceWidth: number, targetWidth: number) {
    const clampedX = Math.max(0, Math.min(sourceWidth, 10_000)); // Clamps x between 0 and 10,000
    const scaleFactor = 0.9 + (0.5 - 0.9) * (clampedX / 10_000);
    return targetWidth >= sourceWidth * scaleFactor;
  }

  transform(
    version: AssetVersionDto,
    asset: AssetDto,
    status: string,
    targetWidth: number,
    targetHeight: number,
    watermark: boolean,
    options: {
      forCanvas?: boolean;
      allowUpScale?: boolean;
    } = {
      forCanvas: false,
      allowUpScale: true,
    },
    page = 0
  ): string {
    if (!canDisplayAsset(asset, version)) return '';

    const source = Rect.fromObject(getAssetDimentions(version, undefined, page));
    const target = new Rect(0, 0, targetWidth, targetHeight);

    const { width, height } = this.getTargetDimensions(version.uuid, target, source, options);

    // we use the base image instead of getting the resize lambda to sweat when we get close enough to the target size
    // Close enough is calculated using a scale factor that goes between 0.9 and 0.5 depending on the size of the base image.
    if (this.shouldUseBase(source.width, width)) {
      const baseTooBig = options.forCanvas && Math.max(source.width, source.height) > getMaxWebGlTextureSize();
      if (!baseTooBig) return calcBaseUrl(this.config.assetsEndpoint, asset, version, watermark, page);
    }

    const longEdge = Math.max(width, height);
    const params = sizesToAlwaysIncludeMetadataFor.includes(longEdge) ? `l${longEdge}mi` : `l${longEdge}`;

    return calcResizedUrl(
      this.config.assetsEndpoint,
      asset,
      version,
      params,
      watermark,
      specialCaseFormats.includes(getExtension(version.asset_name)),
      undefined,
      page
    );
  }
}
